import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIMEZONE,
  FLOATING_TIMEZONE,
  FULL_DATETIME_FORMAT,
} from 'core/constants';

import { isRenderableZone, timezoneStore } from 'stores/Timezone';

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

// These read the zone store: call them during render, never inside useMemo or useCallback.

export type DateTimeValue = string | number | Date | Dayjs | null | undefined;

export interface TimezoneOption {
  zone: string;
  label: string;
  searchText: string;
}

const TZ_FREE_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TZ_FREE_TIME = /^\d{1,2}:\d{2}(:\d{2})?$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const OFFSET_SUFFIX = /(Z|[+-]\d{2}(:?\d{2})?)$/i;
const BRACKETED_TEXT = /\[[^\]]*\]/g;
const DATE_TOKENS = /[YMDdQWwGgXx]/;
const TIME_TOKENS = /[HhkmsSAaXx]/;
const ZONE_TOKENS = /[Zz]/;

// Not `dayjs(value, 'HH:mm')`: that needs customParseFormat, which only MUI's adapter constructor registers.
export const parseTzFreeTime = (value: string): Dayjs | null => {
  const [rawHour, rawMinute, ...rest] = value.split(':');
  const hour = Number(rawHour);
  const minute = Number(rawMinute);
  const outOfRange = hour < 0 || hour > 23 || minute < 0 || minute > 59;
  if (rest.length || !Number.isInteger(hour) || !Number.isInteger(minute) || outOfRange) {
    return null;
  }
  // UTC anchor: a local one normalises a wall clock inside a DST gap, reading '01:00' back as 02:00.
  return dayjs.utc().hour(hour).minute(minute).second(0).millisecond(0);
};

const isBlank = (value: DateTimeValue): value is null | undefined | '' =>
  value === null || value === undefined || value === '';

export const isTzFreeDate = (value: DateTimeValue): value is string =>
  typeof value === 'string' && TZ_FREE_DATE.test(value);

const isTzFree = (value: DateTimeValue): value is string =>
  typeof value === 'string' && (TZ_FREE_DATE.test(value) || TZ_FREE_TIME.test(value));

export const isNaiveDateTime = (value: DateTimeValue): value is string =>
  typeof value === 'string' && ISO_DATE_TIME.test(value) && !OFFSET_SUFFIX.test(value.slice(10));

const formatTzFree = (value: string, pattern: string): string => {
  const tokens = pattern.replace(BRACKETED_TEXT, '');
  const asksForZone = ZONE_TOKENS.test(tokens);
  if (TZ_FREE_DATE.test(value)) {
    return dayjs.utc(value).format(asksForZone || TIME_TOKENS.test(tokens) ? DEFAULT_DATE_FORMAT : pattern);
  }
  const time = parseTzFreeTime(value);
  if (!time) return value;
  return time.format(asksForZone || DATE_TOKENS.test(tokens) ? DEFAULT_TIME_FORMAT : pattern);
};

const formatAsParsed = (value: string | number | Date | Dayjs, pattern: string): string => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(pattern) : '';
};

const renderableOrDefault = (zone: string): string => (isRenderableZone(zone) ? zone : DEFAULT_TIMEZONE);

const offsetLabel = (minutes: number): string => {
  if (minutes === 0) return 'GMT';
  const hours = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.abs(minutes) % 60;
  return `GMT${minutes > 0 ? '+' : '-'}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
};

const offsetMinutes = (zone: string, at: string | number | Date | Dayjs): number => dayjs(at).tz(zone).utcOffset();

export const zoneOffsetLabel = (zone: string, at: string | number | Date | Dayjs = new Date()): string =>
  dayjs(at).isValid() ? offsetLabel(offsetMinutes(renderableOrDefault(zone), at)) : '';

export const parseMoment = (value: string | number | Date | Dayjs): Dayjs | null => {
  if (isNaiveDateTime(value)) return dayjs.utc(value).isValid() ? dayjs.tz(value, DEFAULT_TIMEZONE) : null;
  const moment = dayjs(value);
  return moment.isValid() ? moment : null;
};

export const formatMomentInZone = (value: DateTimeValue, zone: string, pattern: string): string => {
  if (isBlank(value)) return '';
  if (isTzFree(value)) return formatTzFree(value, pattern);
  const moment = parseMoment(value);
  if (!moment) return '';
  const inZone = moment.tz(renderableOrDefault(zone));
  return `${inZone.format(pattern)} ${offsetLabel(inZone.utcOffset())}`;
};

export const formatMoment = (value: DateTimeValue, pattern: string = FULL_DATETIME_FORMAT): string => {
  if (timezoneStore.enabled) return formatMomentInZone(value, timezoneStore.readerZone, pattern);
  if (isBlank(value)) return '';
  return isTzFree(value) ? formatTzFree(value, pattern) : formatAsParsed(value, pattern);
};

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

export const readerDayKey = (value: DateTimeValue): string => {
  if (isBlank(value)) return '';
  if (isTzFreeDate(value)) return value;
  if (!timezoneStore.enabled) return formatAsParsed(value, DAY_KEY_FORMAT);
  const moment = parseMoment(value);
  return moment ? moment.tz(renderableOrDefault(timezoneStore.readerZone)).format(DAY_KEY_FORMAT) : '';
};

export const formatFloating = (
  value: DateTimeValue,
  kind: 'date' | 'time',
  pattern: string = kind === 'date' ? DEFAULT_DATE_FORMAT : DEFAULT_TIME_FORMAT
): string => {
  if (isBlank(value)) return '';
  if (isTzFree(value)) return formatTzFree(value, pattern);
  if (!timezoneStore.enabled) return formatAsParsed(value, pattern);
  if (isNaiveDateTime(value)) {
    const typed = dayjs.utc(value);
    return typed.isValid() ? typed.format(pattern) : '';
  }
  const moment = dayjs(value);
  return moment.isValid() ? moment.tz(FLOATING_TIMEZONE).format(pattern) : '';
};

export const formatMomentDate = (value: DateTimeValue, pattern: string = DEFAULT_DATE_FORMAT): string =>
  formatFloating(readerDayKey(value), 'date', pattern);

export type ReaderPeriodUnit = 'day' | 'isoWeek' | 'month';

interface ReaderPeriod {
  from: number;
  to: number;
}

// Calendar arithmetic runs on UTC days, which have no clock changes; only each day's start reads in the zone.
export const readerPeriod = (unit: ReaderPeriodUnit, at: string | number | Date | Dayjs = Date.now()): ReaderPeriod => {
  if (!timezoneStore.enabled) {
    return { from: dayjs(at).startOf(unit).valueOf(), to: dayjs(at).endOf(unit).valueOf() };
  }
  const zone = renderableOrDefault(timezoneStore.readerZone);
  const day = dayjs.utc(dayjs(at).tz(zone).format(DAY_KEY_FORMAT));
  const first = unit === 'isoWeek' ? day.subtract(day.isoWeekday() - 1, 'day') : day.startOf(unit);
  const next = unit === 'isoWeek' ? first.add(7, 'day') : first.add(1, unit);
  const dayStart = (calendarDay: Dayjs): number => dayjs.tz(calendarDay.format(DAY_KEY_FORMAT), zone).valueOf();
  return { from: dayStart(first), to: dayStart(next) - 1 };
};

export const utcMidnight = (day: string): number => dayjs.utc(day).valueOf();

export type ExportTimeZoneParams = { time_zone: string } | Record<string, never>;

// Read when an export starts. Never `{ time_zone: undefined }`: BaseAPI.open would send it as "undefined".
export const exportTimeZoneParams = (legacy?: string): ExportTimeZoneParams => {
  if (timezoneStore.enabled) return { time_zone: timezoneStore.readerZone };
  return legacy ? { time_zone: legacy } : {};
};

export const zoneCity = (zone: string): string =>
  (zone.split('/').pop() ?? zone).replace(/_/g, ' ').replace('St Johns', "St John's");

const longOffset = (minutes: number): string => {
  const hours = String(Math.floor(Math.abs(minutes) / 60)).padStart(2, '0');
  const rest = String(Math.abs(minutes) % 60).padStart(2, '0');
  return `GMT${minutes < 0 ? '-' : '+'}${hours}:${rest}`;
};

const labelAt = (zone: string, minutes: number): string => `(${longOffset(minutes)}) ${zoneCity(zone)}`;

export const zoneLabel = (zone: string, at: Date = new Date()): string =>
  isRenderableZone(zone) ? labelAt(zone, offsetMinutes(zone, at)) : zoneCity(zone);

const genericName = (zone: string, at: Date): string => {
  try {
    return (
      new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'longGeneric' })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
};

const buildOption = (zone: string, label: string, at: Date): TimezoneOption => ({
  zone,
  label,
  searchText: [label, zone, zone.replace(/_/g, ' '), genericName(zone, at)].join(' '),
});

export const timezoneOption = (zone: string, at: Date = new Date()): TimezoneOption =>
  buildOption(zone, zoneLabel(zone, at), at);

const HOUR_MS = 60 * 60 * 1000;

let cachedOptions: { hour: number; options: TimezoneOption[] } | undefined;

export const timezoneOptions = (): TimezoneOption[] => {
  const at = new Date();
  const hour = Math.floor(at.getTime() / HOUR_MS);
  if (cachedOptions?.hour === hour) return cachedOptions.options;
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const withUtc = zones.length && !zones.includes('UTC') ? [...zones, 'UTC'] : zones;
  const options = withUtc
    .map((zone) => {
      const minutes = offsetMinutes(zone, at);
      return { minutes, option: buildOption(zone, labelAt(zone, minutes), at) };
    })
    .sort((a, b) => a.minutes - b.minutes || a.option.label.localeCompare(b.option.label))
    .map(({ option }) => option);
  cachedOptions = { hour, options };
  return options;
};
